'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import Link from 'next/link';
import { Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';

const GUIDE_IDS = ['first-post', 'social-media-calculator', 'hashtag-research'] as const;

export default function TutorialsPage() {
  return (
    <PublicPageLayout>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" component="h1" fontWeight={700} mb={1} color="text.primary">
          Guides & Tutorials
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Step-by-step written guides to get the most out of FloAI. Video tutorials are planned for later.
        </Typography>

        <Box component="nav" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>On this page</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <MuiLink href="#first-post" color="primary" sx={{ display: 'block' }}>Your first post</MuiLink>
            <MuiLink href="#social-media-calculator" color="primary" sx={{ display: 'block' }}>Using the Social Media Calculator</MuiLink>
            <MuiLink href="#hashtag-research" color="primary" sx={{ display: 'block' }}>Hashtag Research in 5 minutes</MuiLink>
          </Box>
        </Box>

        <Typography component="h2" id={GUIDE_IDS[0]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Your first post
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            1. Sign in and connect at least one social account from your <MuiLink component={Link} href="/dashboard/profile" color="primary">Profile → Connected Accounts</MuiLink>.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Go to <MuiLink component={Link} href="/dashboard/create" color="primary">Create</MuiLink>. Write your caption or paste content, then choose the platforms you want to publish to.
          </Typography>
          <Typography variant="body1" paragraph>
            3. Pick “Publish now” or “Schedule” and set the date/time. Click to publish or schedule. Your first post is done.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tip: Use the Content Calendar on your dashboard to plan posts by date, or try <MuiLink component={Link} href="/tools/calendar-templates" color="primary">Calendar templates</MuiLink> for campaigns.
          </Typography>
        </Paper>

        <Typography component="h2" id={GUIDE_IDS[1]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Using the Social Media Calculator
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            The <MuiLink component={Link} href="/tools/social-media-calculator" color="primary">Social Media Calculator</MuiLink> helps you estimate the value and ROI of your social presence. Enter your follower counts and engagement (or import from CSV if you have it). The tool shows estimated reach, engagement value, and time savings. Use it to set baselines before campaigns or to compare channels. Results can feed into the Social Media Audit for deeper analysis.
          </Typography>
        </Paper>

        <Typography component="h2" id={GUIDE_IDS[2]} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
          Hashtag Research in 5 minutes
        </Typography>
        <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            1. Open <MuiLink component={Link} href="/tools/hashtag-research" color="primary">Hashtag Research</MuiLink> and enter a topic or seed hashtag in the search box.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Review the suggested hashtags and their metrics (e.g. volume, relevance). Pick a mix of broad and niche tags.
          </Typography>
          <Typography variant="body1" paragraph>
            3. Use “Add to content” or copy the list into your Create page or calendar. You can also use the AI suggestions to get caption ideas tied to your chosen hashtags.
          </Typography>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          More guides and video tutorials are coming soon. In the meantime, check <MuiLink component={Link} href="/getting-started" color="primary">Getting Started</MuiLink> and the <MuiLink component={Link} href="/faq" color="primary">FAQ</MuiLink>.
        </Typography>
      </Container>
    </PublicPageLayout>
  );
}
